import { html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles as componentStyles } from '../view/cardGroupStyles.scss';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';
import { GzSelectionCardWebComponent } from './GzSelectionCardWebComponent';
import { SelectionCardEvents, SelectionCardEventType } from '../types';

/**
 * @summary This component handles the radio card select event and updates the selected card based on the internal card group value
 * @property name - The name of the card group
 * @property value - The value of the card group. Can be a string or a number or null
 * @property selected - The selected state of the selection card
 * @property disabled - The disabled state of the selection card
 * @slot This is the default slot that can be used to display the cards. The content should only consist of gz-selection-card web components. It can be set in the component via inner content (inserted between the '<gz-selection-card-group>' tags)
 * @tag gz-selection-card-group
 * @event {SelectionCardEventType} change - fires when a card inside the group is selected via user action. The event contains the id and value of the selected card.
 */
@customElement('gz-selection-card-group')
export class GzSelectionCardGroupWebComponent extends FormFieldInput<string | number | null> {
  static get styles() {
    return [unsafeCSS(`${componentStyles}`)];
  }

  @property({ type: String, attribute: true, reflect: true })
  name: string;

  @property({ attribute: true, reflect: true })
  value: string | number | null = null;

  public setFormValue(value: string | number | null): void {
    this.value = value;
  }
  public getFormValue(): string | number | null {
    return this.value;
  }

  protected updated(changedProperties: Map<string | number, string | number>) {
    super.updated(changedProperties);
    if (changedProperties.has('value')) {
      this.selectCard(this.value);
    }
  }

  /**
   * Get all cards inside this group
   */
  private get selectableCards(): GzSelectionCardWebComponent[] {
    return Array.from<GzSelectionCardWebComponent>(this.querySelectorAll('gz-selection-card'));
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'radiogroup');
    this.setAttribute('aria-labelledby', this.name);
    this.setAttribute('aria-label', this.name);
    this.addEventListener(SelectionCardEvents.CARD_SELECTED, (e: SelectionCardEventType) => this.onCardClicked(e));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(SelectionCardEvents.CARD_SELECTED, (e: SelectionCardEventType) => this.onCardClicked(e));
  }

  render() {
    return html` <slot></slot> `;
  }

  private onCardClicked(event: SelectionCardEventType): void {
    event.stopPropagation();
    const { id, value } = event.detail;
    this.selectCard(value);
    this.value = value;
    this.dispatchEvent(
      new SelectionCardEventType(SelectionCardEvents.CHANGE, {
        detail: {
          id,
          value: this.value,
        },
      })
    );
  }

  /**
   * Updates the selected card based on the internal radio group value
   * @param value
   * @private
   */
  private selectCard(value: string | number | null): void {
    this.selectableCards.forEach((card: GzSelectionCardWebComponent) => {
      card.selected = card.value === value;
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-selection-card-group': GzSelectionCardGroupWebComponent;
  }
}
